import React, { useRef, useState } from 'react';
import {
  Box,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Popover,
  Typography,
} from '@mui/material';
import {
  AttachFile,
  Audiotrack,
  Close,
  Description,
  Image as ImageIcon,
  InsertEmoticon,
  PhotoCamera,
  Send,
} from '@mui/icons-material';
import { ATTACH_ACCEPT, formatBytes, inspectAttachment } from './mediaFile';
import { filterEmojis } from './emojiSet';
import { wa } from './waTheme';

const ATTACH_ITEMS = [
  { id: 'document', label: 'Document', accept: ATTACH_ACCEPT.document, icon: <Description />, color: '#7f66ff' },
  { id: 'media', label: 'Photos & videos', accept: ATTACH_ACCEPT.media, icon: <ImageIcon />, color: '#007bfc' },
  { id: 'camera', label: 'Camera', accept: ATTACH_ACCEPT.camera, capture: 'environment', icon: <PhotoCamera />, color: '#ff2e74' },
  { id: 'audio', label: 'Audio', accept: ATTACH_ACCEPT.audio, icon: <Audiotrack />, color: '#ff8a19' },
];

export default function WhatsAppComposer({
  draft,
  onDraft,
  onSendText,
  onSendFile,
  onRejectFile,
  sending,
}) {
  const inputRef = useRef(null);
  const fileRefs = useRef({});
  const [attachEl, setAttachEl] = useState(null);
  const [emojiEl, setEmojiEl] = useState(null);
  const [emojiQuery, setEmojiQuery] = useState('');
  const [pending, setPending] = useState(null);

  const clearPending = () => {
    setPending((current) => {
      if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
      return null;
    });
  };

  const insertEmoji = (emoji) => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? draft.length;
    const end = el?.selectionEnd ?? draft.length;
    const next = `${draft.slice(0, start)}${emoji}${draft.slice(end)}`;
    onDraft(next);
    requestAnimationFrame(() => {
      el?.focus();
      const pos = start + emoji.length;
      el?.setSelectionRange(pos, pos);
    });
  };

  const chooseFile = (file) => {
    if (!file) return;
    const inspected = inspectAttachment({
      filename: file.name,
      mimeType: file.type,
      size: file.size,
    });
    if (!inspected.ok) {
      onRejectFile?.(inspected.message);
      return;
    }
    clearPending();
    const previewUrl = inspected.kind === 'image' || inspected.kind === 'video'
      ? URL.createObjectURL(file)
      : '';
    setPending({ file, kind: inspected.kind, previewUrl });
  };

  const send = async () => {
    if (sending) return;
    try {
      if (pending) {
        await onSendFile(pending.file, draft.trim());
        clearPending();
        return;
      }
      if (draft.trim()) await onSendText(draft.trim());
    } catch (_) {
      /* parent shows the error */
    }
  };

  const canSend = !sending && (Boolean(pending) || Boolean(draft.trim()));
  const groups = filterEmojis(emojiQuery);

  return (
    <Box sx={{ bgcolor: wa.header, px: 1.5, py: 1, borderTop: `1px solid ${wa.border}` }}>
      {pending && (
        <Box
          sx={{
            mb: 1,
            mx: 0.5,
            p: 1,
            bgcolor: '#fff',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            boxShadow: '0 1px 2px rgba(11,20,26,.08)',
          }}
        >
          {pending.kind === 'image' && pending.previewUrl && (
            <Box component="img" src={pending.previewUrl} alt="" sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1 }} />
          )}
          {pending.kind === 'video' && pending.previewUrl && (
            <Box component="video" src={pending.previewUrl} muted sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, bgcolor: '#000' }} />
          )}
          {(pending.kind === 'audio' || pending.kind === 'voice' || pending.kind === 'document') && (
            <Box sx={{ width: 42, height: 42, borderRadius: 1, bgcolor: wa.header, display: 'grid', placeItems: 'center', color: wa.icon }}>
              {pending.kind === 'document' ? <Description /> : <Audiotrack />}
            </Box>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontSize: 14, color: wa.text }}>{pending.file.name}</Typography>
            <Typography sx={{ fontSize: 12, color: wa.meta }}>
              {pending.kind === 'voice' ? 'Voice message' : pending.kind} · {formatBytes(pending.file.size)}
            </Typography>
          </Box>
          <IconButton aria-label="Remove file" onClick={clearPending} disabled={sending} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
        <IconButton aria-label="Emoji" onClick={(e) => setEmojiEl(e.currentTarget)} disabled={sending} sx={{ color: wa.icon }}>
          <InsertEmoticon />
        </IconButton>
        <IconButton aria-label="Attach" onClick={(e) => setAttachEl(e.currentTarget)} disabled={sending} sx={{ color: wa.icon }}>
          <AttachFile sx={{ transform: 'rotate(45deg)' }} />
        </IconButton>
        <InputBase
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={5}
          placeholder={pending ? 'Add a caption' : 'Type a message'}
          value={draft}
          disabled={sending}
          onChange={(e) => onDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (canSend) send();
            }
          }}
          sx={{
            px: 1.75,
            py: 1,
            bgcolor: '#fff',
            borderRadius: 2,
            fontSize: 15,
            color: wa.text,
          }}
        />
        <IconButton
          aria-label="Send"
          disabled={!canSend}
          onClick={send}
          sx={{
            bgcolor: canSend ? wa.send : '#e9edef',
            color: canSend ? '#fff' : wa.meta,
            width: 42,
            height: 42,
            mb: 0.25,
            '&:hover': { bgcolor: canSend ? '#008f72' : '#e9edef' },
            '&.Mui-disabled': { bgcolor: '#e9edef', color: wa.meta },
          }}
        >
          <Send sx={{ fontSize: 20, ml: 0.25 }} />
        </IconButton>
      </Box>

      <Menu
        anchorEl={attachEl}
        open={Boolean(attachEl)}
        onClose={() => setAttachEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {ATTACH_ITEMS.map((item) => (
          <MenuItem
            key={item.id}
            onClick={() => {
              setAttachEl(null);
              fileRefs.current[item.id]?.click();
            }}
            sx={{ gap: 1.5, py: 1, minWidth: 220 }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: item.color,
                color: '#fff',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {item.icon}
            </Box>
            <Typography sx={{ color: wa.text }}>{item.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
      {ATTACH_ITEMS.map((item) => (
        <input
          key={item.id}
          ref={(node) => { fileRefs.current[item.id] = node; }}
          type="file"
          hidden
          accept={item.accept}
          capture={item.capture}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            chooseFile(file);
          }}
        />
      ))}

      <Popover
        open={Boolean(emojiEl)}
        anchorEl={emojiEl}
        onClose={() => { setEmojiEl(null); setEmojiQuery(''); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ width: 320, p: 1 }}>
          <InputBase
            autoFocus
            placeholder="Search emoji"
            value={emojiQuery}
            onChange={(e) => setEmojiQuery(e.target.value)}
            sx={{ px: 1, py: 0.5, mb: 1, width: '100%', bgcolor: wa.header, borderRadius: 1, fontSize: 14 }}
          />
          <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
            {groups.map((group) => (
              <Box key={group.id} sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 12, color: wa.meta, px: 0.5, mb: 0.5 }}>{group.label}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                  {group.emojis.map(([emoji]) => (
                    <IconButton
                      key={emoji}
                      onClick={() => insertEmoji(emoji)}
                      sx={{ width: 36, height: 36, fontSize: 22 }}
                    >
                      {emoji}
                    </IconButton>
                  ))}
                </Box>
              </Box>
            ))}
            {groups.length === 0 && (
              <Typography sx={{ fontSize: 13, color: wa.meta, px: 0.5, py: 1 }}>No emoji found</Typography>
            )}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
